/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, CommerceProperty, CommercePropertyValue, IContext } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    IKrannichCBusinessSignUpConfig,
    IKrannichCBusinessSignUpProps,
    IKrannichCBusinessSignUpResources
} from './krannich-c-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
// todo: need to add relevant source for radio button
import { IBusinessSignUpRadioButtonOptionProps /*, BusinessSignUpRadioButtonOption*/ } from './components/sign-up-radio-button';
import { kRV_SubscribeNewsLetterAsync } from '../../actions/customProxy/NewsLetter/DataActionExtension.g';
import { IKRV_SubscribeNewsLetter } from '../../actions/customProxy/NewsLetter/DataServiceEntities.g';

export interface IBusinessSignUpExtendedProps extends IKrannichCBusinessSignUpProps<IKrannichCBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    isTermsConditionCheck: boolean;
    isNewsletterCheck: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    hasStatusMessage: string;
    hasStatusError: boolean;
    errorMessage: string;
    abnValue: string;
    isAbnValidCheck: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
    getStatusMessage: React.ReactNode;
    termsConditions: React.ReactNode;
    newsletter: React.ReactNode;
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    Number = 'Number',
    RadioButton = 'radiobutton'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    ProspectType = 'ProspectType'
}
export enum InputIDMex {
    Name = 'Name',
    FirstLastName = 'FirstLastName',
    SecondLastName = 'SecondLastName',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    InvoicingRFC = 'InvoicingRFC',
    VatID = 'VatID',
    TaxRegime = 'TaxRegime',
    Purpose = 'Purpose',
    Email = 'CompanyEmail',
    Comment = 'Comment',
    ProspectType = 'ProspectType'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    resources: IKrannichCBusinessSignUpResources,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void,
    errorMessage?: string
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const { id, type, label, maxChars, isRequired, pattern, customClass, value, fieldReference } = element;
        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else {
            baseSignUpItem.input = (
                <>
                    <SignUpInput
                        {...{
                            id,
                            type,
                            value,
                            maxLength: maxChars,
                            pattern,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onInputChange,
                            fieldReference
                        }}
                    />
                    {value.length === 0 && isRequired === true && showErrors ? (
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                            {resources.requiredFieldMissingError}
                        </span>
                    ) : null}
                    {errorMessage !== '' && id === 'JobTitle' ? (
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                            {errorMessage}
                        </span>
                    ) : null}
                    {baseSignUpItem.id === InputID.Email && !EmailRegex.defaultRegex.test(value) && value.length > 0 ? (
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                            {resources.invalidEmailAddressValidationMessage}
                        </span>
                    ) : null}
                </>
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IKrannichCBusinessSignUpProps<IKrannichCBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label:
                this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForUsSite
                    ? this.props.resources.companyLabelForUS
                    : this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.abnTitleLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            maxChars: '11'
        },
        {
            id: InputID.Size,
            type: InputType.Text,
            label: this.props.resources.companySizeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        }
    ];
    public BusinessSignUpMexicoInputs: IFormItem[] = [
        {
            id: InputIDMex.Name,
            type: InputType.Text,
            label: this.props.resources.namesLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputIDMex.FirstLastName,
            type: InputType.Text,
            label: this.props.resources.lastNameMexLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputIDMex.InvoicingRFC,
            type: InputType.Text,
            label: this.props.resources.invoicingRfcLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputIDMex.TaxRegime,
            type: InputType.Text,
            label: this.props.resources.taxRegimeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputIDMex.Purpose,
            type: InputType.Text,
            label: this.props.resources.purposeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        }
    ];
    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'ms-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            hasStatusMessage: '',
            hasStatusError: false,
            isTermsConditionCheck: false,
            isNewsletterCheck: false,
            errorMessage: '',
            abnValue: '',
            isAbnValidCheck: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.updateFormItems = this.updateFormItems.bind(this);
    }

    public componentDidMount(): void {
        const {
            context: {
                actionContext: {
                    requestContext: { query }
                }
            }
        } = this.props;
        if (this.props.context.request.user.isAuthenticated) {
            const successPageURL = getUrlSync('home', this.props.context.actionContext);
            window.location.href = String(successPageURL);
        }
        const ItemId = query?.ItemId;
        const Name = query?.Name;
        const producerKeyName = query?.producerKeyName;
        const producer = query?.producer;
        const usSiteFlag =
            this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForUsSite;
        const mexicoSiteFlag =
            this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForMexicoSite;
        if (ItemId && Name && producer) {
            const updatedForm = this.state.formItems;
            updatedForm.forEach(item => {
                if (item.id === InputID.Comment) {
                    item.value = `${'Item Number: '}${ItemId}${'\n'}${'Product Name: '}${Name}${'\n'}${producerKeyName ||
                        'Manufacturer'}: ${producer}`;
                }
            });
            this.setState({
                formItems: updatedForm
            });
        }
        this.formChildReference.current?.focus();
        // VSI Customization for Florida Shop ( label change : Post code  to Zip code )
        const parentElement = document.getElementById('company_addresszipcode_container');
        const childElement = parentElement?.firstChild;
        if (parentElement && childElement) {
            const newLabel = document.createElement('label');
            newLabel.className = 'msc-address-form__label';
            newLabel.htmlFor = 'company_addresszipcode';
            newLabel.textContent = `${usSiteFlag || mexicoSiteFlag ? 'Zip Code *' : 'Postal Code *'}`;
            childElement?.replaceWith(newLabel);
        }
        if (mexicoSiteFlag) {
            // VSI Customization for Mexico Shop ( label change : Street * -> Department/Area * )
            // Select the label element by its for attribute
            const labelElement = document.querySelector('label[for="company_addressstreet"]');
            // Check if the label element exists
            if (labelElement) {
                // Update the text content
                labelElement.textContent = 'Department/Area *';
            }
        }
    }
    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    //VSI CUSTOMIZATION FOR DISPLAY STATUS MESSAGE
    public _getStatusMessage() {
        return this.state.hasStatusError ? <p>{this.state.hasStatusMessage}</p> : null;
    }
    public render(): JSX.Element {
        const { config, resources } = this.props;
        const { formItems, isNewsletterCheck, isTermsConditionCheck } = this.state;
        const { className, heading } = config;
        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems,
            getStatusMessage: this._getStatusMessage(),
            termsConditions: (
                <div className='terms-and-conditions'>
                    <input
                        className='checkbox-terms'
                        type='checkbox'
                        aria-label='terms and conditions'
                        required
                        defaultChecked={isTermsConditionCheck}
                        onChange={() => this.setState({ isTermsConditionCheck: !isTermsConditionCheck })}
                    />
                    <div className='terms-text'>
                        {this.props.resources.termsandConditionStatementFirstPart}
                        {
                            <>
                                {this.props.config.termsandConditions && this.props.config.termsandConditions.linkText && (
                                    <RichTextComponent />
                                )}{' '}
                                &nbsp;
                                <a
                                    title='Terms and Conditions'
                                    className='conditions-link'
                                    href={
                                        this.props.config.termsandConditions && this.props.config.termsandConditions.linkUrl.destinationUrl
                                    }
                                    target={this.props.config.termsandConditions?.openInNewTab ? '_blank' : ''}
                                    rel='noreferrer'
                                >
                                    {this.props.resources.termsandConditions}
                                </a>
                            </>
                        }
                        {this.props.resources.termsandConditionStatementSecondPart}
                    </div>
                </div>
            ),
            newsletter: (
                <div className='newsletter-sign-up'>
                    <label>
                        <input
                            tabIndex={0}
                            className='checkbox-newsletter'
                            type='checkbox'
                            defaultChecked={isNewsletterCheck}
                            onChange={() => this.setState({ isNewsletterCheck: !isNewsletterCheck })}
                        />
                        {resources.newsLetterSub}
                    </label>
                </div>
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems, isTermsConditionCheck, isAbnValidCheck } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText
            // errorMessage,
            // partialAddressErrorMessage
        } = resources;
        const { currentRegionName, ounRegions } = this.props.context?.app.config;
        const currentRegion = ounRegions?.find((region: { Name: any }) => region.Name === currentRegionName);
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                resources,
                this.needsToReset,
                this._onInputChange,
                showAddressErrors ?? hasSubmitErrors,
                addressForm,
                this._onAddressChange,
                this.state.errorMessage
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={
                        !isReady ||
                        !isTermsConditionCheck ||
                        (this.props?.context?.actionContext?.requestContext?.apiSettings?.oun ===
                            this.props.context.app.config.ounNumberForUsSite ||
                        (currentRegion && this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === currentRegion?.oun)
                            ? undefined
                            : !isAbnValidCheck)
                    }
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                // hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={this.state.errorMessage} />
                // ,showAddressErrors && (
                //     <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                //  )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const newForm = this.state.formItems;
        //VSI Customization for ABN field validation starts
        if (event.target.id === 'JobTitle') {
            const phoneReg = /^[0-9][0-9]*$/;
            let jobTitleId: number = 5;
            //getting array index for abn field
            for (let i = 0; i < newForm.length; i++) {
                if (newForm[Number(i)].id === 'JobTitle') {
                    jobTitleId = i;
                }
            }
            const tempValue = event.target.value.split(' ').join('');
            //Checking if entered string matches criteria
            if (
                (newForm[Number(jobTitleId)].value.length <= 14 && phoneReg.test(tempValue)) ||
                newForm[Number(jobTitleId)].value.length > event.target.value.length
            ) {
                newForm[Number(jobTitleId)].value = tempValue;
                this.setState({
                    errorMessage: this.props.resources.abnLengthMessage,
                    isAbnValidCheck: false
                });
            }

            //if 11 digits reached adding spaces
            if (newForm[Number(jobTitleId)].value.length === 11) {
                this.setState(
                    {
                        errorMessage: '',
                        abnValue: newForm[Number(jobTitleId)].value
                    },
                    () => {
                        this.updateFormItems(jobTitleId);
                        this.setState({ isAbnValidCheck: true });
                    }
                );
            }
            //if 11 digits reached adding spaces
            if (newForm[Number(jobTitleId)].value.length === 0) {
                this.setState({
                    errorMessage: '',
                    abnValue: newForm[Number(jobTitleId)].value
                });
            }
            //VSI Customization End
        } else {
            newForm.forEach(item => {
                if (item.id === event.target.id) {
                    item.value = event.target.value;
                }
            });
        }
        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    public updateFormItems = (jobTitleId: number): void => {
        //VSI customization to Format ABN Values i.e adding spaces
        if (this.state.formItems) {
            const newForm = this.state.formItems;
            const newValue = this.state.abnValue;
            const formattedValue = `${newValue.slice(0, 2)} ${newValue.slice(2, 5)} ${newValue.slice(5, 8)} ${newValue.slice(8)}`;
            newForm[Number(jobTitleId)].value = formattedValue;
            this.setState({
                formItems: newForm,
                isAbnValidCheck: true
            });
        }
    };

    private _addedReference(): IFormItem[] {
        if (this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForUsSite) {
            //Customization for removal of ABN field from Form for US Site
            this.BusinessSignUpDefaultInputs = this.BusinessSignUpDefaultInputs.filter(
                obj => obj.id !== 'JobTitle' && obj.id !== 'CompanySize'
            );
        }
        let newFormFields;
        if (this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForMexicoSite) {
            newFormFields = this.BusinessSignUpMexicoInputs;
        } else {
            newFormFields = this.BusinessSignUpDefaultInputs;
        }
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;
        var state: any = document.getElementById('company_addressstate');
        var city: any = document.getElementById('company_addresscity');
        var street: any = document.getElementById('company_addressstreet');
        var postCode: any = document.getElementById('company_addresszipcode');
        var Phone: any = document.getElementById('company_addressphone');

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern
            if (state.value === '' || city.value === '' || street.value === '' || postCode.value === '' || Phone.value === '') {
                return false;
            } else if (currentInput.type === InputType.Address) {
                if (this.state.addressHasErrors) {
                    if (state.value !== '') {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else if (currentInput.isRequired) {
                if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };
    private readonly _newsletterCall = async (ctx: IContext, data: IKRV_SubscribeNewsLetter) => {
        await kRV_SubscribeNewsLetterAsync(ctx, data);
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        event.preventDefault();
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true,
                hasSubmitErrors: true
            });
        }

        const { config, context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
            companyAddress.address.Phone = companyAddress.address.Phone?.split(' ').join('');
        }
        if (
            companyAddress &&
            companyAddress.address &&
            this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForUsSite
        ) {
            const phoneNum = companyAddress.address.Phone;
            const countryCode = 1;
            // Apply formatting
            const areaCode = phoneNum?.slice(0, 3);
            const firstPart = phoneNum?.slice(3, 6);
            const secondPart = phoneNum?.slice(6);
            companyAddress.address.Phone = `${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
        }
        const newValue: CommercePropertyValue = {
            BooleanValue: undefined,
            ByteValue: undefined,
            DateTimeOffsetValue: undefined,
            DecimalValue: undefined,
            IntegerValue: undefined,
            LongValue: undefined,
            StringValue:
                this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForUsSite
                    ? ''
                    : jobTitle && jobTitle.value
        };
        const newExtension: CommerceProperty = {
            Key: 'ABN',
            Value: newValue
        };
        let newPartner: BusinessPartnerProspect;
        if (this.props?.context?.actionContext?.requestContext?.apiSettings?.oun === this.props.context.app.config.ounNumberForMexicoSite) {
            // for Mexico
            const firstName = this._getItem(InputIDMex.Name);
            const firstLastName = this._getItem(InputIDMex.FirstLastName);
            const invoicingRFC = this._getItem(InputIDMex.InvoicingRFC);
            const taxRegime = this._getItem(InputIDMex.TaxRegime);
            const purpose = this._getItem(InputIDMex.Purpose);

            const invoicingRFCValue: CommercePropertyValue = {
                BooleanValue: undefined,
                ByteValue: undefined,
                DateTimeOffsetValue: undefined,
                DecimalValue: undefined,
                IntegerValue: undefined,
                LongValue: undefined,
                StringValue: invoicingRFC?.value
            };

            const invoicingRFCObject: CommerceProperty = {
                Key: 'invoicingRFC',
                Value: invoicingRFCValue
            };

            const taxRegimeValue: CommercePropertyValue = {
                BooleanValue: undefined,
                ByteValue: undefined,
                DateTimeOffsetValue: undefined,
                DecimalValue: undefined,
                IntegerValue: undefined,
                LongValue: undefined,
                StringValue: taxRegime?.value
            };
            const taxRegimeObject: CommerceProperty = {
                Key: 'taxRegime',
                Value: taxRegimeValue
            };
            const purposeValue: CommercePropertyValue = {
                BooleanValue: undefined,
                ByteValue: undefined,
                DateTimeOffsetValue: undefined,
                DecimalValue: undefined,
                IntegerValue: undefined,
                LongValue: undefined,
                StringValue: purpose?.value
            };
            const purposeObject: CommerceProperty = {
                Key: 'purpose',
                Value: purposeValue
            };

            newPartner = {
                FirstName: firstName && firstName.value,
                LastName: firstLastName && firstLastName.value,
                CompanyName: companyName && companyName.value,
                Address: companyAddress && companyAddress.address && companyAddress.address,
                Department: department && department.value,
                CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
                Comments: comments && comments.value,
                ExtensionProperties: [invoicingRFCObject, taxRegimeObject, purposeObject]
            };
        } else {
            newPartner = {
                FirstName: firstName && firstName.value,
                LastName: lastName && lastName.value,
                CompanyName: companyName && companyName.value,
                Address: companyAddress && companyAddress.address && companyAddress.address,
                Department: department && department.value,
                CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
                Comments: comments && comments.value,
                ExtensionProperties: [newExtension]
            };
        }

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(res => {
                if (res && res.ExtensionProperties && res.ExtensionProperties?.length === 1) {
                    const successPageURL =
                        (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                        getUrlSync('home', this.props.context.actionContext);
                    if (this.state.isNewsletterCheck) {
                        const subscribeNewsLetter = {
                            Company: newPartner.CompanyName,
                            FirstName: newPartner.FirstName,
                            LastName: newPartner.LastName,
                            ZipCode: newPartner.Address?.ZipCode,
                            EmailAddress: newPartner.Address?.Email || ''
                        };
                        const ctx = {
                            callerContext: context.actionContext
                        };
                        //calling newsletter subscribtion API
                        void this._newsletterCall(ctx, subscribeNewsLetter);
                    }
                    this.setState({ isNewsletterCheck: false, isTermsConditionCheck: false });
                    window.location.href = String(successPageURL);
                } else {
                    res &&
                        res.ExtensionProperties &&
                        res?.ExtensionProperties?.map(e => {
                            if (e.Key === 'Error' && e.Value?.StringValue === 'ABN is missing') {
                                this.setState({
                                    hasStatusMessage: e.Value?.StringValue,
                                    hasStatusError: true
                                });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else if (e.Key === 'ProspectStatus' && e.Value?.StringValue === 'Pending') {
                                this.setState({
                                    hasStatusMessage: this.props.resources.userPendingStateMessage,
                                    hasStatusError: true
                                });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else if (e.Key === 'ProspectStatus' && e.Value?.StringValue === 'Approved') {
                                this.setState({
                                    hasStatusMessage: this.props.resources.accountAlreadyExistMessage,
                                    hasStatusError: true
                                });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else if (e.Key === 'ProspectStatus' && e.Value?.StringValue === 'Active') {
                                this.setState({
                                    hasStatusMessage: this.props.resources.userAlreadyExistAndActivatedMessage,
                                    hasStatusError: true
                                });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        });
                }
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: false,
                    isReady: false
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isTermsConditionCheck: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
